<script setup lang="ts">
/** Phase 17.10: the upgrade prompt when a locked feature is used (set by usePlan().guard or a 402 from the API). */
import { useI18n } from 'vue-i18n';
import { ADVANCED_FEATURES } from '@codot/contracts';
import { useRouter } from 'vue-router';
import { upgradePrompt } from '../../stores/plan';
import { useAuthStore } from '../../stores/auth.store';
import AdvancedBadge from './AdvancedBadge.vue';

const { t } = useI18n();
const router = useRouter();
const auth = useAuthStore();
function upgrade(): void {
  upgradePrompt.value = null;
  void router.push({ name: 'organization-settings', query: { tab: 'subscription', plan: 'ADVANCED' } });
}
</script>

<template>
  <div
    v-if="upgradePrompt"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4"
    role="dialog"
    aria-modal="true"
    :aria-label="t('plan.dialogTitle')"
    @click.self="upgradePrompt = null"
    @keydown.esc="upgradePrompt = null"
  >
    <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
      <AdvancedBadge />
      <h2 class="mt-2 text-lg font-semibold">{{ t('plan.lockedTitle', { feature: t(`plan.f_${upgradePrompt}`) }) }}</h2>
      <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">{{ t(`plan.d_${upgradePrompt}`) }}</p>
      <p class="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">{{ t('plan.alsoIncluded') }}</p>
      <ul class="mt-1 grid grid-cols-1 gap-x-3 gap-y-1 text-sm sm:grid-cols-2">
        <li v-for="f in ADVANCED_FEATURES" :key="f" class="flex items-center gap-1.5" :class="f === upgradePrompt ? 'font-medium' : 'text-slate-600 dark:text-slate-300'">
          <svg viewBox="0 0 16 16" class="h-3.5 w-3.5 shrink-0 text-emerald-600" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m3.5 8.5 3 3 6-7" /></svg>
          {{ t(`plan.f_${f}`) }}
        </li>
      </ul>
      <div class="mt-6 flex flex-wrap items-center justify-end gap-2">
        <p v-if="auth.role !== 'OWNER'" class="me-auto text-xs text-slate-500">{{ t('plan.askOwner') }}</p>
        <button type="button" class="btn-secondary" @click="upgradePrompt = null">{{ t('plan.notNow') }}</button>
        <button v-if="auth.role === 'OWNER'" type="button" class="btn-primary" @click="upgrade">{{ t('plan.seePlans') }}</button>
      </div>
    </div>
  </div>
</template>
